import 'chart.js/auto'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Bar, Line } from 'react-chartjs-2'
import RangeSwitch from './RangeSwitch'
import { SkeletonBar } from './DashboardSkeletonBar'
import { schoolSurface } from './schoolEquipmentChrome'
import { fetchDoorSeries } from '../../api/hikvision'
import { formatChartDateTime, formatChartTick, formatDuration } from '../../lib/formatDuration'
import {
  closeAvailabilityToNow,
  collapseAvailabilitySegments,
  prometheusToAvailability,
  rebootsFromUptime,
  uptimeRatio,
} from '../../lib/nvrAvailability'
import {
  buildAvailabilityChartData,
  buildChannelsChartData,
  buildEventsChartData,
  buildTriggersChartData,
} from '../../lib/deviceAvailabilityCharts'

export const hikvisionTimeRanges = [
  { id: '1h', label: '1 час' },
  { id: '6h', label: '6 часов' },
  { id: '24h', label: '24 часа' },
  { id: '7d', label: '7 дней' },
  { id: '30d', label: '30 дней' },
]

const AvailabilityStrip = ({ segments, plotInset, stripHint, setStripHint, upRatio, outages, t }) => (
  <div className="mb-4">
    <div className="mb-2 flex items-baseline justify-between text-xs">
      <span className="text-muted-400">
        {stripHint ? (
          <>
            {stripHint.v === 1
              ? t('equipment.state.available')
              : stripHint.v === 0
                ? t('equipment.state.unavailable')
                : t('common.noData')}
            <span className="text-muted-600"> · </span>
            <span className="tabular-nums text-white">
              {formatChartDateTime(stripHint.from)}
              {stripHint.to !== stripHint.from ? ` – ${formatChartDateTime(stripHint.to)}` : ''}
            </span>
          </>
        ) : (
          <>
            {t('device.uptime')} <span className="text-white tabular-nums">{Math.round(upRatio * 100)}%</span>
            <span className="text-muted-600"> · </span>
            {t('device.outages', { count: outages })}
          </>
        )}
      </span>
    </div>
    <div
      className="flex h-5 cursor-pointer items-center gap-px"
      style={{ marginLeft: plotInset.left, marginRight: plotInset.right }}
      onMouseLeave={() => setStripHint(null)}
    >
      {segments.map((segment, index) => {
        const hovered = stripHint === segment
        const tone =
          segment.v === 1
            ? hovered
              ? 'bg-positive'
              : 'bg-positive/70'
            : segment.v === 0
              ? hovered
                ? 'bg-critical'
                : 'bg-critical/70'
              : hovered
                ? 'bg-muted-400'
                : 'bg-muted-600'
        return (
          <div
            key={`${segment.from}-${index}`}
            style={{ flexGrow: Math.max(segment.to - segment.from, 1) }}
            className={`first:rounded-l-full last:rounded-r-full transition-[height,background-color] duration-150 ${
              hovered ? 'z-10 h-4' : 'h-2.5'
            } ${tone}`}
            onMouseEnter={() => setStripHint(segment)}
          />
        )
      })}
    </div>
  </div>
)

const RebootHistory = ({ reboots, hasUptime, t }) => (
  <div className="mt-5 border-t border-white/[0.06] pt-4">
    <h3 className="font-display text-sm text-white">{t('nvr.rebootHistory')}</h3>
    {!hasUptime ? (
      <p className="mt-3 text-sm text-muted-400">{t('nvr.rebootNoUptime')}</p>
    ) : reboots.length === 0 ? (
      <p className="mt-3 text-sm text-muted-400">{t('nvr.rebootEmpty')}</p>
    ) : (
      <ul className="mt-3 max-h-72 space-y-1 overflow-y-auto">
        {reboots.map((reboot, index) => {
          const ranFor = formatDuration(reboot.at - reboot.ranForSec * 1000, reboot.at)
          return (
            <li
              key={`${reboot.at}-${index}`}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-xl px-2 py-2 text-sm hover:bg-white/[0.03]"
            >
              <span className="h-1.5 w-1.5 shrink-0 translate-y-[-1px] self-center rounded-full bg-warning" />
              <span className="tabular-nums text-white">{formatChartDateTime(reboot.at)}</span>
              {ranFor && (
                <span className="text-xs text-muted-400">{t('nvr.rebootRanBefore', { duration: ranFor })}</span>
              )}
            </li>
          )
        })}
      </ul>
    )}
  </div>
)

const DeviceAvailabilityAnalytics = ({
  t,
  deviceId,
  currentUp,
  downSince,
  isDown = false,
  refreshKey = 0,
  modes,
  secondaryKind,
  eventDoorId = '1',
  emptyMessage,
  stripOnly = false,
  showRebootHistory = false,
}) => {
  const [timeRange, setTimeRange] = useState('24h')
  const [activeChart, setActiveChart] = useState(modes[0]?.id || 'availability')
  const [series, setSeries] = useState(null)
  const [seriesBusy, setSeriesBusy] = useState(false)
  const [seriesError, setSeriesError] = useState(null)
  const [stripHint, setStripHint] = useState(null)
  const [plotInset, setPlotInset] = useState({ left: 40, right: 8 })
  const plotInsetRef = useRef({ left: 40, right: 8 })
  const isAvailability = activeChart === 'availability'
  const activeMode = modes.find((mode) => mode.id === activeChart) || modes[0]
  const secondaryType = secondaryKind === 'events' || secondaryKind === 'triggers' ? 'bar' : 'line'

  useEffect(() => {
    if (!deviceId) return undefined
    let cancelled = false
    const load = async () => {
      setSeriesBusy(true)
      setSeriesError(null)
      try {
        const data = await fetchDoorSeries(deviceId, timeRange)
        if (!cancelled) setSeries(data)
      } catch (err) {
        if (!cancelled) {
          setSeriesError(err?.response?.data?.message || err?.message || 'Нет данных для отображения за выбранный период.')
        }
      } finally {
        if (!cancelled) setSeriesBusy(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [deviceId, timeRange, refreshKey])

  const liveUp = currentUp === 1 || currentUp === 0 ? currentUp : isDown ? 0 : currentUp
  const availabilityPoints = useMemo(
    () =>
      series
        ? closeAvailabilityToNow(prometheusToAvailability(series?.series?.up || []), {
            nowMs: Date.now(),
            currentUp: liveUp,
            downSince,
          })
        : [],
    [series, liveUp, downSince]
  )
  const availabilitySegments = useMemo(() => {
    const fromMs = series?.from != null ? Number(series.from) * 1000 : null
    const stepMs = series?.step != null ? Number(series.step) * 1000 : null
    return collapseAvailabilitySegments(availabilityPoints, null, {
      fromMs: Number.isFinite(fromMs) ? fromMs : null,
      maxGapMs: Number.isFinite(stepMs) && stepMs > 0 ? stepMs * 2 : null,
    })
  }, [availabilityPoints, series])
  const upRatio = uptimeRatio(availabilityPoints)
  const outages = availabilitySegments.filter((segment) => segment.v === 0).length
  const uptimeSeries = series?.series?.uptime_seconds
  const reboots = useMemo(
    () => (showRebootHistory ? rebootsFromUptime(uptimeSeries || []) : []),
    [showRebootHistory, uptimeSeries]
  )

  const availabilityChartData = useMemo(
    () => buildAvailabilityChartData(availabilityPoints, series?.series?.scrape_duration_seconds, t('device.responseTime')),
    [availabilityPoints, series, t]
  )

  const secondaryChartData = useMemo(() => {
    if (secondaryKind === 'channels') return buildChannelsChartData(series?.series)
    if (secondaryKind === 'events') return buildEventsChartData(series?.series, eventDoorId)
    if (secondaryKind === 'triggers') return buildTriggersChartData(series?.series)
    return null
  }, [secondaryKind, series, eventDoorId])

  const chartOptions = useMemo(() => {
    const withDate = timeRange === '24h' || timeRange === '7d' || timeRange === '30d'
    const stacked = !isAvailability && secondaryType === 'bar'
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { bottom: 10 } },
      scales: {
        x: {
          grid: { display: false },
          stacked,
          afterBuildTicks(axis) {
            const limit = withDate ? 8 : 12
            const ticks = axis.ticks
            if (!ticks?.length || ticks.length <= limit) return
            const picked = []
            const seen = new Set()
            for (let i = 0; i < limit; i += 1) {
              const idx = Math.round((i * (ticks.length - 1)) / (limit - 1))
              if (seen.has(idx)) continue
              seen.add(idx)
              picked.push(ticks[idx])
            }
            axis.ticks = picked
          },
          ticks: {
            color: '#94a3b8',
            maxRotation: 0,
            autoSkip: false,
            font: { size: 11 },
            callback(value) {
              return formatChartTick(Number(this.getLabelForValue(value)), { withDate })
            },
          },
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          min: 0,
          stacked,
          grid: { color: 'rgba(255,255,255,0.04)' },
          ticks: {
            color: '#94a3b8',
            callback: isAvailability ? (value) => `${value} с` : undefined,
          },
        },
      },
      plugins: {
        legend: {
          display: !isAvailability,
          labels: { color: '#cbd5f5', usePointStyle: true, padding: 16, pointStyle: 'rectRounded' },
        },
        tooltip: {
          backgroundColor: 'rgba(15,17,28,0.92)',
          titleColor: '#f8fafc',
          bodyColor: '#e2e8f0',
          borderColor: 'rgba(148, 163, 184, 0.25)',
          borderWidth: 1,
          padding: 12,
          cornerRadius: 12,
          usePointStyle: true,
          boxWidth: 6,
          boxHeight: 6,
          callbacks: {
            title(items) {
              if (!items?.length) return ''
              return formatChartDateTime(Number(items[0].label)) || ''
            },
            label(item) {
              if (item.raw == null) return null
              const n = Number(item.raw)
              if (!Number.isFinite(n)) return item.dataset.label
              const unit = isAvailability ? ' с' : ''
              return `${item.dataset.label}: ${n.toFixed(2)}${unit}`
            },
          },
        },
      },
    }
  }, [isAvailability, secondaryType, timeRange])

  const plotInsetPlugin = useMemo(
    () => ({
      id: 'devicePlotInset',
      afterLayout(chart) {
        const area = chart.chartArea
        if (!area) return
        const next = {
          left: Math.round(area.left),
          right: Math.round(chart.width - area.right),
        }
        const prev = plotInsetRef.current
        if (prev.left === next.left && prev.right === next.right) return
        plotInsetRef.current = next
        requestAnimationFrame(() => setPlotInset(next))
      },
    }),
    []
  )

  const empty = (
    <div
      className={`flex items-center justify-center px-6 text-center text-sm text-muted-400 ${
        stripOnly ? 'min-h-16' : 'h-full'
      }`}
    >
      {seriesError || emptyMessage}
    </div>
  )

  return (
    <section className={`rounded-3xl p-6 ${schoolSurface.section}`}>
      <header className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <h2 className="font-display text-lg text-white">{activeMode?.title}</h2>
          </div>
          {modes?.length > 1 && (
            <RangeSwitch ranges={modes} value={activeChart} onChange={setActiveChart} />
          )}
        </div>
        <RangeSwitch ranges={hikvisionTimeRanges} value={timeRange} onChange={setTimeRange} />
      </header>

      <div className="relative rounded-3xl bg-surface-2 p-5">
        {seriesBusy && (
          <div
            className="pointer-events-none absolute inset-0 z-10 rounded-3xl bg-surface-2/35"
            aria-busy="true"
            aria-live="polite"
          >
            <span className="absolute right-4 top-4 inline-flex h-5 w-5 animate-spin rounded-full border-2 border-brand-300/25 border-t-brand-300" />
          </div>
        )}
        {isAvailability && availabilityPoints.length > 0 && (
          <AvailabilityStrip
            segments={availabilitySegments}
            plotInset={stripOnly ? { left: 0, right: 0 } : plotInset}
            stripHint={stripHint}
            setStripHint={setStripHint}
            upRatio={upRatio}
            outages={outages}
            t={t}
          />
        )}
        {stripOnly ? (
          availabilityPoints.length === 0 && (
            !series && seriesBusy ? (
              <SkeletonBar className="h-5 rounded-full" />
            ) : (
              empty
            )
          )
        ) : (
          <div className="h-80">
            {!series && seriesBusy ? (
              <SkeletonBar className="h-full rounded-2xl" />
            ) : isAvailability
              ? availabilityChartData
                ? (
                  <Line data={availabilityChartData} options={chartOptions} plugins={[plotInsetPlugin]} />
                )
                : empty
              : secondaryChartData
                ? secondaryType === 'bar'
                  ? (
                    <Bar data={secondaryChartData} options={chartOptions} plugins={[plotInsetPlugin]} />
                  )
                  : (
                    <Line data={secondaryChartData} options={chartOptions} plugins={[plotInsetPlugin]} />
                  )
                : empty}
          </div>
        )}
        {showRebootHistory && isAvailability && series && (
          <RebootHistory
            reboots={reboots}
            hasUptime={Array.isArray(uptimeSeries) && uptimeSeries.length > 0}
            t={t}
          />
        )}
      </div>
    </section>
  )
}

export default DeviceAvailabilityAnalytics
